import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Icon, Button, Card, CarInfoBox, Badge, SectionHeader,
  ListItem, ReservationSummary,
  GuestInfoSection, OptionsSection, PaymentInfoSection,
  TourScheduleSection, Spinner,
} from '../../../components/common';
import { reservationService, pricingService, paymentService } from '../../../api/services';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { useMoney } from '../../../hooks/useMoney';
import { translateApiError } from '../../../utils/apiError';

export default function TourComplete() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // 라우트 :id 는 투어 id 이므로 예약 조회에 쓰면 안 됨 — 예약 id 는 state(데스크탑) 또는
  // ?id= 쿼리(모바일 redirect 복귀)에서 가져온다. (TransferComplete 와 동일 패턴)
  const stateReservationId = location.state?.reservationId;
  // 무통장입금: TourPayment 가 navigate state 로 넘긴 가상계좌 정보. 있으면 '입금 대기' 로 표기.
  const vbankInfo = location.state?.vbank || null;
  const [data, setData] = useState(location.state?.booking || null);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState(null);
  const [vehicleData, setVehicleData] = useState({});
  const [payFailed, setPayFailed] = useState(false);

  useEffect(() => {
    if (data) return;
    // 모바일 결제 redirect 복귀: 실패/취소 시 PortOne 이 code 파라미터를 붙여 보냄.
    //   code 가 있으면 결제가 안 된 것 → 완료 페이지로 렌더하지 않음(취소했는데 '결제 완료' 오표시 방지).
    if (searchParams.get('code')) { setPayFailed(true); setLoading(false); return; }
    const reservationId = stateReservationId || searchParams.get('id');
    if (!reservationId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        let res = await reservationService.getById(reservationId);
        // 모바일 redirect 복귀: 결제 성공인데 아직 Payment 미생성(웹훅 미반영)이면 confirm 으로
        //   확정 처리(예약 confirmed + Payment 생성 → 결제수단·일시 표기). 이미 있으면 skip(중복 방지).
        const paymentId = searchParams.get('paymentId');
        const failCode = searchParams.get('code');
        if (paymentId && !failCode && !res.payment) {
          try {
            await paymentService.confirm({
              order_id: paymentId,
              payment_key: searchParams.get('transactionId') || searchParams.get('txId') || undefined,
              amount: res.total_price,
            });
            res = await reservationService.getById(reservationId);
          } catch (_) { /* 웹훅이 처리하므로 confirm 실패해도 무시 */ }
        }
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(translateApiError(err, t, 'errors.generic'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [data, stateReservationId, searchParams, t]);

  useEffect(() => {
    pricingService.getVehicleInfo('tour').then(setVehicleData).catch((e) => console.warn(e.message));
    pricingService.getServiceSurcharges('tour').then((s) => {
      if (s?.overtime || s?.overtime_price) setVehicleData((prev) => ({ ...prev, _surcharge: s.overtime, _surchargeOvertimePrice: Number(s.overtime_price) || 0 }));
    }).catch((e) => console.warn(e.message));
  }, []);

  // BE 가 한국어로 내려주는 투어명/차량명/스펙/초과요금 안내를 현재 UI 언어로 DeepL 번역.
  const carNameSource = data?.vehicle_name || data?.vehicle?.name || data?.vehicle || vehicleData.carName || '';
  const localizableBundle = useMemo(() => {
    const bag = [];
    if (data?.tour_name || data?.title) bag.push(data.tour_name || data.title);
    if (carNameSource) bag.push(carNameSource);
    if (vehicleData.seat) bag.push(vehicleData.seat);
    if (vehicleData.capacity) bag.push(vehicleData.capacity);
    if (vehicleData.luggage) bag.push(vehicleData.luggage);
    if (vehicleData._surcharge) bag.push(vehicleData._surcharge);
    return bag;
  }, [data?.tour_name, data?.title, carNameSource, vehicleData]);
  const localizedList = useDeepLTranslated(localizableBundle);
  const tx = useMemo(() => {
    const m = new Map();
    localizableBundle.forEach((src, i) => { m.set(src, localizedList[i] || src); });
    return (s) => (typeof s === 'string' ? (m.get(s) || s) : s);
  }, [localizableBundle, localizedList]);

  if (payFailed) return (
    <><Navbar />
      <div className="bg-mono-50 min-h-screen flex flex-col items-center justify-center gap-5 px-5 text-center">
        <p className="text-base font-medium text-mono-700 break-keep">{t('payment.alert_payment_cancelled')}</p>
        {/* 돌아가기 → 결제 페이지로 복귀(입력값은 sessionStorage 캐시로 복원, 결제 재시도 가능) */}
        <Button level="level1" width="fixed-140" onClick={() => navigate(`/tour/${id}/payment`)}>{t('common.go_back')}</Button>
      </div>
    </>
  );
  if (loading) return <><Navbar /><div className="flex justify-center py-8"><Spinner /></div></>;
  if (error) return <><Navbar /><p className="text-sm text-red-500 text-center py-8">{error}</p></>;
  if (!data) return <><Navbar /><p className="text-sm text-mono-400 text-center py-8">{t('complete.no_reservation_data')}</p></>;

  const paymentInfo = data.payment || {};
  const totalPrice = data.total_price ?? paymentInfo.amount ?? 0;
  const baseHours = data.base_hours || 2;
  // getById(ReservationDetail) 응답 필드 매핑 — 부킹 스냅샷 필드명과 달라 정규화(없으면 폴백).
  const route = data.route || {};
  const tourTitle = data.tour_name || data.title || '';
  const customerName = data.booker_name || data.customer_name || '';
  const customerPhone = data.booker_phone || data.phone || '';
  const customerEmail = data.booker_email || data.email || '';
  const departureAddr = route.departure?.address || data.departure || '';
  const arrivalAddr = route.destination?.address || data.arrival || '';
  const scheduledDate = (route.reservation_date && route.departure?.time)
    ? `${route.reservation_date} ${String(route.departure.time).slice(0, 5)}`
    : (data.scheduled_date || '');
  const departureNotes = route.departure?.memo || data.departure_notes || '';
  const stopovers = route.stopovers || data.stopovers || [];
  const peopleQty = data.passengers ?? data.passenger_count ?? 0;

  return (
    <>
      <Navbar />
      <main className="bg-mono-50 min-h-screen pb-28">
        <div className="max-w-content mx-auto px-2.5 sm:px-5 py-5 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header ===== */}
          <div className="text-center space-y-2 py-4">
            <h1 className="text-xl font-bold text-orange-500">{t('complete.reservation_complete_title')}</h1>
            <p className="text-2sm font-normal text-mono-500">{vbankInfo ? t('complete.vbank_complete_desc') : t('complete.reservation_complete_desc')}</p>
          </div>

          {/* ===== Badges + meta ===== */}
          <Card>
            <ReservationSummary
              service={t('nav.private_tour')}
              serviceType="tour"
              status={vbankInfo ? t('complete.vbank_status') : ({ pending: t('reservation.pending'), confirmed: t('reservation.confirmed'), completed: t('reservation.completed'), cancelled: t('reservation.cancelled'), refunded: t('reservation.refunded') }[data.status] || data.status || t('reservation.confirmed'))}
              paymentDate={data.created_at ? `${new Date(data.created_at).toLocaleDateString('ko-KR')} ${t('complete.reservation_paid')}` : ''}
              title={`${t('nav.private_tour')} - ${tx(tourTitle)}`}
              reservationNo={data.reservation_number}
              scheduledDate={scheduledDate}
            />
          </Card>

          {/* ===== 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('complete.vehicle_info')} />
            <CarInfoBox
              images={vehicleData.images || []}
              carName={tx(carNameSource)}
              usageTimeBadge={t('form.base_time_hours', { hours: baseHours })}
              specs={[
                { icon: 'van', text: tx(vehicleData.seat) },
                { icon: 'person', text: tx(vehicleData.capacity) },
                { icon: 'bag', text: tx(vehicleData.luggage) },
              ]}
            >
              {(vehicleData._surchargeOvertimePrice > 0 || vehicleData._surcharge) && (
                <div className="flex items-center gap-2">
                  <Icon name="time" size={18} className="text-mono-500 shrink-0" />
                  {/* #272: 초과요금 통화 환산 — overtime_price 우선 useMoney (KRW 문자열 폴백) */}
                  <span className="text-2sm font-medium text-mono-700">{vehicleData._surchargeOvertimePrice > 0
                    ? t('booking.overtime_fee_per_hour', { amount: money(vehicleData._surchargeOvertimePrice) })
                    : tx(vehicleData._surcharge)}</span>
                </div>
              )}
            </CarInfoBox>
          </Card>

          {/* ===== 예약자 정보 ===== */}
          <Card>
            <GuestInfoSection
              name={customerName}
              phone={customerPhone}
              email={customerEmail}
            />
          </Card>

          {/* ===== 선택 옵션 ===== */}
          <Card>
            <OptionsSection options={[
              { label: t('tour.use_passengers'), value: `${peopleQty}${t('common.people')}`, price: `(+${money(0)})` },
              // #233: 예약에 저장된 선택 옵션 동적 노출 (이동 플로우 TransferPayment 와 동일 — selected_options 기준)
              ...(data.selected_options || []).map((o) => ({
                label: o.name,
                value: (o.quantity || 0) > 1 ? `${o.quantity}${o.unit || ''}` : '',
                price: `( +${money(Number(o.total_price) || 0)} )`,
              })),
            ]} />
          </Card>

          {/* ===== 투어 일정 정보 ===== */}
          <Card>
            <TourScheduleSection
              departure={{ address: departureAddr, time: scheduledDate, notes: departureNotes }}
              stopovers={stopovers}
              arrival={{ address: arrivalAddr }}
            />
          </Card>

          {/* ===== 결제 정보 ===== */}
          <Card>
            <PaymentInfoSection
              date={paymentInfo.paid_at?.slice(0, 16)?.replace('T', ' ') || paymentInfo.date || (data.created_at ? new Date(data.created_at).toLocaleDateString('ko-KR') : '')}
              method={vbankInfo ? t('complete.vbank_pending') : (paymentInfo.card_name || paymentInfo.method || '')}
              amount={paymentInfo.amount != null ? money(Number(paymentInfo.amount)) : ''}
              totalAmount={totalPrice != null ? money(Number(totalPrice)) : ''}
              currency=""
            />
          </Card>

          {/* ===== 무통장입금 가상계좌 안내 (입금 대기) ===== */}
          {vbankInfo && (
            <Card>
              <SectionHeader variant="card" title={t('complete.vbank_info')} />
              <div className="space-y-2">
                <ListItem label={t('complete.vbank_bank')} value={vbankInfo.vbank_name} layout="inline" />
                <ListItem label={t('complete.vbank_num')} value={vbankInfo.vbank_num} layout="inline" />
                <ListItem label={t('complete.vbank_holder')} value={vbankInfo.vbank_holder} layout="inline" />
                {vbankInfo.vbank_date && (
                  <ListItem label={t('complete.vbank_date')} value={new Date(vbankInfo.vbank_date * 1000).toLocaleString('ko-KR')} layout="inline" />
                )}
              </div>
              <p className="text-2sm font-medium text-orange-500 mt-2 break-keep">{t('complete.vbank_notice')}</p>
            </Card>
          )}

          {/* ===== Bottom action ===== */}
          <Button level="level2" fullWidth onClick={() => navigate('/reservation')}>
            {t('complete.view_reservation')}
          </Button>

        </div>
      </main>
    </>
  );
}
